/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { isPast, secondsToMilliseconds } from 'date-fns';

import { DEVICE_TYPE } from 'config/api';
import { LANGUAGE_KEYS } from 'config/languageKeys';

import { StreamTypes } from 'store/player/actions.types';
import { selectStandardChannelById } from 'store/programs/selectors';
import {
    EpgProgram,
    PlaybackTypes,
    RBProgram,
} from 'store/railsBuilder/reducer.types';
import {
    cancelRecording,
    cancelSeriesRecording,
    deleteRecording,
    deleteSeriesRecording,
    getQuotaUsage,
    getRecordingsCache,
    protectRecording,
    scheduleRecording,
    scheduleSeriesRecording,
    unprotectRecording,
} from 'store/recordings/actions';
import {
    DeleteRecordingOptions,
    RecordingActionType,
} from 'store/recordings/actions.types';
import {
    NPVRAction,
    RecordingItem,
    RecordingMetadata,
    SeriesStatus,
} from 'store/recordings/reducer.types';
import {
    selectCachedRecordingByEventId,
    selectCachedRecordingsBySeriesId,
    selectCachedSeriesStatus,
    selectNPVRActionsData,
    selectSeriesRecordingsHasProtectedRecording,
} from 'store/recordings/selectors';
import { ReduxStore } from 'store/types';
import { selectLanguage } from 'store/user/selectors';

import { VodEditorial } from 'services/data-manager/types';
import useGetScheduleRecordingsParams from 'services/uav/useGetScheduleRecordingsParams';
import { logUavAnalytics } from 'services/uav/utils';

import getChannelId from 'utils/getChannelId';
import getCorrectProgramStartAndEndTime from 'utils/getCorrectProgramStartAndEndTime';
import {
    RECORDING_BUTTONS,
    hasRemainingProtectedSpace,
    isFailed,
    isProtectedRecording,
    isRecorded,
    isRecording,
    isRecordingItem,
    isValidRecording,
    selectRecordingMetadataBaseOnLanguage,
} from 'utils/recordings';
import { isWeb } from 'utils/responsiveness';

import {
    DELETED_EPISODE_NOTIFICATION_TITLE,
    DELETED_NOTIFICATION_TITLE,
    PROTECTED_NOTIFICATION_TITLE,
    RECORDING_NOTIFICATION_SCHEDULED_CANCELLED,
    RECORDING_NOTIFICATION_SCHEDULED_STARTED,
    RECORDING_NOTIFICATION_SCHEDULED_STOPPED,
    RECORDING_NOTIFICATION_SERIES_CANCELLED,
    RECORDING_NOTIFICATION_SERIES_SCHEDULED,
    RECORDING_NOTIFICATION_TIMEOUT,
    SCREEN_ALERT_QUOTA_EXCEEDED_BODY,
    SCREEN_ALERT_QUOTA_EXCEEDED_TITLE,
    UNPROTECT_NOTIFICATION_TITLE,
} from 'screens/Detail/languageConst';
import {
    ManageRecordingItem,
    ManageRecordingItemKeys,
    PinDialogType,
} from 'screens/Detail/types';
import { isLive } from 'screens/Epg/utils/program';
import { onBeforeShowPlayerModal } from 'screens/PlayerModal/utils';

import { AppNotification } from 'components/Notification/types';
import { StandardUavProps } from 'components/Ribbon/types';

import { RecordingType } from '../../screens/PlayerModal/types';
import useRecordingPermissions from './useRecordingPermissions';

type Params = {
    item: RBProgram | EpgProgram | VodEditorial | RecordingItem | undefined;
    type: RecordingType;
    playbackType?: PlaybackTypes;
    playerVisible?: boolean;
} & StandardUavProps;

type NotificationContent = {
    title: string;
    description?: string;
    errorCode?: number;
    withCustomIcon?: boolean;
    withoutLoading?: boolean;
};

const RECORDINGS_CACHE_REFRESH_TIMEOUT = 60000;

function useRecordings({ item, type, playbackType, playerVisible }: Params) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const eventId = isRecordingItem(item) ? item.eventId : item?.id ?? '';
    const seriesId =
        (item as RecordingItem)?.seriesId ?? (item as RBProgram)?.seriesRef;

    const currentLanguage = useSelector(selectLanguage);
    const channel = useSelector((state: ReduxStore) =>
        selectStandardChannelById(
            state,
            getChannelId(
                isRecordingItem(item)
                    ? selectRecordingMetadataBaseOnLanguage(
                          item.metadata,
                          currentLanguage,
                      )
                    : item,
            ),
        ),
    );

    const recordingPermissionsState = useRecordingPermissions({
        program: isRecordingItem(item)
            ? selectRecordingMetadataBaseOnLanguage(
                  item.metadata,
                  currentLanguage,
              )
            : (item as EpgProgram | RBProgram | undefined),
        channel,
    });

    const recordingItem = useSelector(selectCachedRecordingByEventId(eventId));
    const seriesRecordingItems = useSelector(
        selectCachedRecordingsBySeriesId(seriesId),
    );
    const seriesHasProtectedRecording = useSelector(
        selectSeriesRecordingsHasProtectedRecording(seriesId),
    );
    const seriesStatus = useSelector(selectCachedSeriesStatus(seriesId));
    const nPVRActions = useSelector(selectNPVRActionsData);

    const recordingIsValid = isValidRecording(recordingItem);
    const recordingIsRecorded = isRecorded(recordingItem);
    const recordingHasStatusRecording = isRecording(recordingItem);
    const recordingIsProtected = isProtectedRecording(recordingItem);
    const recordingIsFailed = isFailed(recordingItem);

    const seriesRecordingIdFromRecordingItem = recordingItem?.seriesRecordingId;
    const recordingItemId = recordingItem?.id;

    const recordingMetadata = selectRecordingMetadataBaseOnLanguage(
        isRecordingItem(item) ? item?.metadata ?? {} : {},
        currentLanguage,
    );

    const isLiveProgram = isRecordingItem(item)
        ? isLive(recordingMetadata)
        : isLive(item);
    const { programStartTime } = getCorrectProgramStartAndEndTime(
        isRecordingItem(item)
            ? selectRecordingMetadataBaseOnLanguage(
                  item.metadata,
                  currentLanguage,
              )
            : item,
    );
    const isAssetInPast =
        programStartTime && isPast(secondsToMilliseconds(programStartTime));

    const { getScheduleRecordingsParams } = useGetScheduleRecordingsParams(
        playbackType as unknown as StreamTypes,
        item,
    );

    const seriesHasRecordedRecordings = useMemo(
        () => !!seriesRecordingItems.some(isRecorded),
        [seriesRecordingItems],
    );

    const canScheduleSeries =
        !!seriesId && seriesStatus !== SeriesStatus.ACTIVE;
    const canCancelSeries = !!seriesId && seriesStatus === SeriesStatus.ACTIVE;
    const canDeleteSeries = !!seriesId && seriesHasRecordedRecordings;

    const [notification, setNotification] = useState<AppNotification>();
    const [showQuotaDialog, setShowQuotaDialog] = useState(false);
    const [showCancelEpisodeDialog, setShowCancelEpisodeDialog] =
        useState(false);
    const [showCancelSeriesDialog, setShowCancelSeriesDialog] = useState(false);
    const [isRecordingLoading, setIsRecordingLoading] = useState(false);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [showDeleteRecordingDialog, setShowDeleteRecordingDialog] =
        useState(false);
    const [showDeleteSeriesDialog, setShowDeleteSeriesDialog] = useState(false);

    const pushRouteInWeb = () => {
        if (isWeb) {
            window.history.pushState({}, '', null);
        }
    };

    const removeRouteInWeb = () => {
        if (isWeb) {
            window.history.back();
        }
    };

    const canWatchRecording =
        recordingPermissionsState.userHasRecordingPermissions &&
        recordingPermissionsState.isRecordingPlayable &&
        recordingIsRecorded;

    const getAssetTitle = useCallback(
        (isSeries?: boolean) => {
            const isItemRecording = isRecordingItem(item);
            const source = isItemRecording ? recordingMetadata : item;

            const episodeTitle = isItemRecording
                ? (source as RecordingMetadata)?.episode ?? undefined
                : (source as RBProgram | EpgProgram | VodEditorial)?.Episode ??
                  undefined;

            if (isSeries) {
                return source?.Title ?? undefined;
            }

            if (seriesId) {
                return episodeTitle;
            }

            return source?.Title ?? undefined;
        },
        [item, recordingMetadata, seriesId],
    );

    const onCallbackSetNotification = useCallback(
        ({
            title,
            description,
            errorCode,
            withCustomIcon = true,
        }: NotificationContent): void => {
            setNotification({
                title,
                description: errorCode
                    ? `ERR_CODE_${errorCode}: ${description}`
                    : description,
                withCustomIcon,
            });
        },
        [],
    );

    const showDropdown = useCallback(() => {
        setIsDropdownVisible(true);
    }, []);

    const hideDropdown = useCallback(() => {
        setIsDropdownVisible(false);
    }, []);

    const resetNotificationCallback = useCallback(() => {
        setNotification(undefined);
    }, []);

    const showCancelEpisodeDialogCallback = useCallback(() => {
        pushRouteInWeb();

        setTimeout(() => {
            setShowCancelEpisodeDialog(true);
        }, 10);
    }, []);

    const hideCancelEpisodeDialogCallback = useCallback(() => {
        removeRouteInWeb();
        setShowCancelEpisodeDialog(false);

        if (isWeb) {
            showDropdown();
        } else {
            hideDropdown();
        }
    }, [showDropdown, hideDropdown]);

    const showCancelSeriesDialogCallback = useCallback(() => {
        pushRouteInWeb();

        setTimeout(() => {
            setShowCancelSeriesDialog(true);
        }, 10);
    }, []);

    const showDeleteRecordingDialogCallback = useCallback(() => {
        if (!playerVisible) {
            pushRouteInWeb();
        }

        setTimeout(() => {
            setShowDeleteRecordingDialog(true);
        }, 10);
    }, [playerVisible]);

    const showDeleteSeriesDialogCallback = useCallback(() => {
        if (!playerVisible) {
            pushRouteInWeb();
        }

        setTimeout(() => {
            setShowDeleteSeriesDialog(true);
        }, 10);
    }, [playerVisible]);

    const hideDeleteRecordingDialogCallback = useCallback(() => {
        if (type !== RecordingType.PLAYER) {
            removeRouteInWeb();
        }
        setShowDeleteRecordingDialog(false);
    }, [type]);

    const hideDeleteSeriesDialogCallback = useCallback(() => {
        if (type !== RecordingType.PLAYER) {
            removeRouteInWeb();
        }
        setShowDeleteSeriesDialog(false);
    }, [type]);

    // Needed for going back in brwoser via browser back button.
    useEffect(() => {
        if (isWeb && !playerVisible) {
            if (showDeleteSeriesDialog) {
                setShowDeleteSeriesDialog(false);
            }

            if (showDeleteRecordingDialog) {
                setShowDeleteRecordingDialog(false);
            }
        }
    }, [playerVisible]);

    const hideCancelSeriesDialogCallback = useCallback(() => {
        removeRouteInWeb();
        setShowCancelSeriesDialog(false);

        if (isWeb) {
            showDropdown();
        } else {
            hideDropdown();
        }
    }, [showDropdown, hideDropdown]);

    const hideQuotaDialogCallback = useCallback(() => {
        if (type !== RecordingType.PLAYER) {
            removeRouteInWeb();
        }
        setIsRecordingLoading(false);
        setShowQuotaDialog(false);
    }, []);

    const handleTimeoutMessage = useCallback(() => {
        onCallbackSetNotification({
            title: t(RECORDING_NOTIFICATION_TIMEOUT),
            withCustomIcon: false,
        });
    }, [t, onCallbackSetNotification]);

    const handleShowQuotaDialog = useCallback(() => {
        if (type !== RecordingType.PLAYER) {
            pushRouteInWeb();
        }
        setShowQuotaDialog(true);
        setIsRecordingLoading(false);
    }, [type]);

    const singleRecordingSchedulingStarted = useRef(false);
    const seriesRecordingSchedulingStarted = useRef(false);

    useEffect(() => {
        if (
            singleRecordingSchedulingStarted.current &&
            recordingItemId &&
            recordingItem
        ) {
            logUavAnalytics(
                getScheduleRecordingsParams({
                    recordingId: recordingItemId,
                    deviceType: DEVICE_TYPE,
                }),
            );
            singleRecordingSchedulingStarted.current = false;
        }
    }, [recordingItemId, recordingItem]);

    useEffect(() => {
        if (seriesRecordingSchedulingStarted.current && seriesId) {
            logUavAnalytics(
                getScheduleRecordingsParams({
                    recordingId: seriesId,
                    deviceType: DEVICE_TYPE,
                    seriesLink: seriesId,
                    seriesId,
                }),
            );
            seriesRecordingSchedulingStarted.current = false;
        }
    }, [seriesId]);

    const handleStartRecording = useCallback(() => {
        singleRecordingSchedulingStarted.current = true;
        const assetTitle = getAssetTitle();

        dispatch(
            scheduleRecording(
                { eventId },
                {
                    onSuccess: () => {
                        dispatch(getRecordingsCache());
                        onCallbackSetNotification({
                            title: t(
                                type === RecordingType.DETAIL
                                    ? isLiveProgram
                                        ? LANGUAGE_KEYS.RECORDING_NOTIFICATION_ONGOING_STARTED
                                        : RECORDING_NOTIFICATION_SCHEDULED_STARTED
                                    : LANGUAGE_KEYS.RECORDING_NOTIFICATION_ONGOING_STARTED,
                            ),
                            description: assetTitle,
                        });
                    },
                    onFailure: () => {
                        dispatch(getRecordingsCache());
                        onCallbackSetNotification({
                            title: t(
                                LANGUAGE_KEYS.RECORDING_NOTIFICATION_NEGATIVE_ACTION,
                            ),
                        });
                    },
                    onTimeout: handleTimeoutMessage,
                },
            ),
        );
    }, [
        getAssetTitle,
        dispatch,
        eventId,
        handleTimeoutMessage,
        onCallbackSetNotification,
        t,
        type,
        isLiveProgram,
    ]);

    const handleCancelRecording = useCallback(() => {
        if (type !== RecordingType.PLAYER) {
            removeRouteInWeb();
        }

        const assetTitle = getAssetTitle();
        setShowCancelEpisodeDialog(false);

        dispatch(
            cancelRecording(
                {
                    recordingId: recordingItemId!,
                    actionType: RecordingActionType.CANCEL,
                },
                {
                    onSuccess: () => {
                        dispatch(getRecordingsCache());
                        onCallbackSetNotification({
                            title: t(
                                isLiveProgram
                                    ? RECORDING_NOTIFICATION_SCHEDULED_STOPPED
                                    : RECORDING_NOTIFICATION_SCHEDULED_CANCELLED,
                            ),
                            description: assetTitle,
                        });
                    },
                    onFailure: () => {
                        dispatch(getRecordingsCache());
                        onCallbackSetNotification({
                            title: t(
                                LANGUAGE_KEYS.RECORDING_NOTIFICATION_NEGATIVE_ACTION,
                            ),
                        });
                    },
                    onTimeout: handleTimeoutMessage,
                },
            ),
        );
    }, [
        type,
        getAssetTitle,
        dispatch,
        recordingItemId,
        handleTimeoutMessage,
        onCallbackSetNotification,
        t,
        isLiveProgram,
    ]);

    const handleDeleteRecording = useCallback(
        (pin?: string) => {
            const assetTitle = getAssetTitle();

            dispatch(
                deleteRecording(
                    {
                        recordingId: recordingItemId!,
                        actionType: RecordingActionType.DELETE,
                        pin,
                    },
                    {
                        onSuccess: () => {
                            dispatch(getRecordingsCache());

                            if (type !== RecordingType.PLAYER) {
                                onCallbackSetNotification({
                                    title: t(
                                        recordingItem?.seriesId
                                            ? DELETED_EPISODE_NOTIFICATION_TITLE
                                            : DELETED_NOTIFICATION_TITLE,
                                    ),
                                    description: assetTitle,
                                });
                            }
                        },
                        onFailure: () => {
                            dispatch(getRecordingsCache());
                            onCallbackSetNotification({
                                title: t(
                                    LANGUAGE_KEYS.RECORDING_NOTIFICATION_NEGATIVE_ACTION,
                                ),
                            });
                        },
                        onTimeout: handleTimeoutMessage,
                    },
                ),
            );
        },
        [
            getAssetTitle,
            dispatch,
            recordingItemId,
            handleTimeoutMessage,
            type,
            onCallbackSetNotification,
            t,
            recordingItem?.seriesId,
        ],
    );

    const handleUnprotectRecording = useCallback(
        (pin: string) => {
            if (type !== RecordingType.PLAYER) {
                removeRouteInWeb();
            }

            if (type === RecordingType.PLAYER) {
                onBeforeShowPlayerModal();
            }

            const assetTitle = getAssetTitle();

            dispatch(
                unprotectRecording(
                    {
                        recordingId: recordingItemId ?? '',
                        pin,
                    },
                    {
                        onSuccess: () => {
                            dispatch(getRecordingsCache());
                            onCallbackSetNotification({
                                title: t(UNPROTECT_NOTIFICATION_TITLE),
                                description: assetTitle,
                            });
                        },
                        onFailure: () => {
                            dispatch(getRecordingsCache());
                            onCallbackSetNotification({
                                title: t(
                                    LANGUAGE_KEYS.RECORDING_NOTIFICATION_NEGATIVE_ACTION,
                                ),
                            });
                        },
                        onTimeout: handleTimeoutMessage,
                    },
                ),
            );
        },
        [
            type,
            getAssetTitle,
            dispatch,
            recordingItemId,
            handleTimeoutMessage,
            onCallbackSetNotification,
            t,
        ],
    );

    const handleProtectRecording = useCallback(() => {
        const assetTitle = getAssetTitle();

        dispatch(
            getQuotaUsage({
                onSuccess: (quota) => {
                    if (
                        hasRemainingProtectedSpace(
                            recordingItem?.captureDuration ?? 0,
                            quota ?? null,
                        )
                    ) {
                        dispatch(
                            protectRecording(
                                { recordingId: recordingItemId! },
                                {
                                    onSuccess: () => {
                                        dispatch(getRecordingsCache());
                                        onCallbackSetNotification({
                                            title: t(
                                                PROTECTED_NOTIFICATION_TITLE,
                                            ),
                                            description: assetTitle,
                                        });
                                    },
                                    onFailure: () => {
                                        dispatch(getRecordingsCache());
                                        onCallbackSetNotification({
                                            title: t(
                                                LANGUAGE_KEYS.RECORDING_NOTIFICATION_NEGATIVE_ACTION,
                                            ),
                                        });
                                    },
                                    onTimeout: handleTimeoutMessage,
                                },
                            ),
                        );
                    } else {
                        handleShowQuotaDialog();
                    }
                },
                onFailure: () => {
                    onCallbackSetNotification({
                        title: t(SCREEN_ALERT_QUOTA_EXCEEDED_TITLE),
                        description: t(SCREEN_ALERT_QUOTA_EXCEEDED_BODY),
                    });
                },
            }),
        );
    }, [
        getAssetTitle,
        dispatch,
        recordingItem?.captureDuration,
        recordingItemId,
        handleTimeoutMessage,
        onCallbackSetNotification,
        t,
        handleShowQuotaDialog,
    ]);

    const handleScheduleSeriesRecording = useCallback(() => {
        if (seriesId) {
            seriesRecordingSchedulingStarted.current = true;
            const assetTitle = getAssetTitle(true);

            dispatch(
                scheduleSeriesRecording(
                    {
                        eventId,
                        seriesId,
                    },
                    {
                        onSuccess: () => {
                            dispatch(getRecordingsCache());
                            onCallbackSetNotification({
                                title: t(
                                    RECORDING_NOTIFICATION_SERIES_SCHEDULED,
                                ),
                                description: assetTitle,
                            });

                            setTimeout(() => {
                                dispatch(getRecordingsCache());
                            }, RECORDINGS_CACHE_REFRESH_TIMEOUT);
                        },
                        onFailure: () => {
                            dispatch(getRecordingsCache());
                            onCallbackSetNotification({
                                title: t(
                                    LANGUAGE_KEYS.RECORDING_NOTIFICATION_NEGATIVE_ACTION,
                                ),
                            });
                        },
                        onTimeout: handleTimeoutMessage,
                    },
                ),
            );
        }
    }, [
        seriesId,
        getAssetTitle,
        dispatch,
        eventId,
        handleTimeoutMessage,
        onCallbackSetNotification,
        t,
    ]);

    const handleCancelSeriesRecording = useCallback(() => {
        if (seriesId) {
            if (type !== RecordingType.PLAYER) {
                removeRouteInWeb();
            }

            const seriesRecordingId =
                seriesRecordingIdFromRecordingItem ||
                seriesRecordingItems[0]?.seriesRecordingId ||
                '';
            const assetTitle = getAssetTitle(true);
            setShowCancelSeriesDialog(false);

            dispatch(
                cancelSeriesRecording(
                    {
                        seriesRecordingId,
                        actionType: RecordingActionType.CANCEL,
                        deleteRecordingOption:
                            DeleteRecordingOptions.UNRECORDED_EPISODES,
                    },
                    {
                        onSuccess: () => {
                            dispatch(getRecordingsCache());
                            onCallbackSetNotification({
                                title: t(
                                    RECORDING_NOTIFICATION_SERIES_CANCELLED,
                                ),
                                description: assetTitle,
                            });
                        },
                        onFailure: () => {
                            dispatch(getRecordingsCache());
                            onCallbackSetNotification({
                                title: t(
                                    LANGUAGE_KEYS.RECORDING_NOTIFICATION_NEGATIVE_ACTION,
                                ),
                            });
                        },
                        onTimeout: handleTimeoutMessage,
                    },
                ),
            );
        }
    }, [
        seriesId,
        type,
        seriesRecordingIdFromRecordingItem,
        seriesRecordingItems,
        getAssetTitle,
        dispatch,
        handleTimeoutMessage,
        onCallbackSetNotification,
        t,
    ]);

    const handleDeleteSeriesRecording = useCallback(
        (pin?: string) => {
            const seriesRecordingId =
                seriesRecordingIdFromRecordingItem ||
                seriesRecordingItems[0]?.seriesRecordingId ||
                '';
            const assetTitle = getAssetTitle(true);

            dispatch(
                deleteSeriesRecording(
                    {
                        seriesRecordingId,
                        actionType: RecordingActionType.DELETE,
                        deleteRecordingOption:
                            DeleteRecordingOptions.DELETE_USER_RECORDINGS_BUT_RETAIN_SERIES_LINK,
                        pin,
                    },
                    {
                        onSuccess: () => {
                            dispatch(getRecordingsCache());

                            if (type !== RecordingType.PLAYER) {
                                onCallbackSetNotification({
                                    title: t(DELETED_NOTIFICATION_TITLE),
                                    description: assetTitle,
                                });
                            }
                        },
                        onFailure: () => {
                            dispatch(getRecordingsCache());
                            onCallbackSetNotification({
                                title: t(
                                    LANGUAGE_KEYS.RECORDING_NOTIFICATION_NEGATIVE_ACTION,
                                ),
                            });
                        },
                        onTimeout: handleTimeoutMessage,
                    },
                ),
            );
        },
        [
            seriesRecordingIdFromRecordingItem,
            seriesRecordingItems,
            getAssetTitle,
            dispatch,
            handleTimeoutMessage,
            type,
            onCallbackSetNotification,
            t,
        ],
    );

    const recordingsButtonsList = useMemo((): ManageRecordingItem[] => {
        const buttonsList: ManageRecordingItem[] = [];

        // Add delete buttons first
        if (recordingIsFailed || (recordingIsValid && recordingIsRecorded)) {
            buttonsList.push(RECORDING_BUTTONS.DeleteRecording);
        }

        if (canDeleteSeries) {
            buttonsList.push(RECORDING_BUTTONS.DeleteSeries);
        }

        // Add record buttons next
        if ((!isAssetInPast || isLiveProgram) && !recordingItem) {
            if (seriesId) {
                buttonsList.push(RECORDING_BUTTONS.ScheduleEpisode);
            } else {
                buttonsList.push(RECORDING_BUTTONS.ScheduleRecording);
            }
        }

        if (canScheduleSeries) {
            buttonsList.push(RECORDING_BUTTONS.ScheduleSeries);
        }

        // Add cancel buttons next
        if (recordingIsValid && !recordingIsRecorded) {
            if (recordingItem?.seriesId) {
                buttonsList.push(RECORDING_BUTTONS.CancelEpisode);
            } else {
                buttonsList.push(RECORDING_BUTTONS.CancelRecording);
            }
        }

        if (canCancelSeries) {
            buttonsList.push(RECORDING_BUTTONS.CancelSeries);
        }

        // Add protect/unprotect buttons last
        if (recordingIsValid && recordingIsRecorded) {
            buttonsList.push(
                recordingIsProtected
                    ? RECORDING_BUTTONS.UnprotectRecording
                    : RECORDING_BUTTONS.ProtectRecording,
            );
        }

        return buttonsList;
    }, [
        recordingIsFailed,
        recordingIsValid,
        recordingIsRecorded,
        isAssetInPast,
        isLiveProgram,
        recordingItem,
        canCancelSeries,
        canDeleteSeries,
        canScheduleSeries,
        seriesId,
        recordingIsProtected,
    ]);

    const onRepeatActionPress = useCallback(
        (actionName: NPVRAction, actionCallback: () => void) => {
            if (
                !nPVRActions.length ||
                nPVRActions.some((action) => action !== actionName)
            ) {
                actionCallback();
            } else {
                handleTimeoutMessage();
            }
        },
        [nPVRActions, handleTimeoutMessage],
    );

    const onManageRecordingPress = useCallback(
        (
            button: ManageRecordingItem,
            setCanCloseButtonDropdown,
            handleShowPinDialog,
        ) => {
            setCanCloseButtonDropdown(false);
            switch (button.key) {
                // single recordings
                case ManageRecordingItemKeys.ScheduleRecording:
                case ManageRecordingItemKeys.ScheduleEpisode:
                    onRepeatActionPress(
                        NPVRAction.SCHEDULE,
                        handleStartRecording,
                    );
                    break;
                case ManageRecordingItemKeys.CancelRecording:
                    onRepeatActionPress(
                        NPVRAction.DELETE,
                        type === RecordingType.PLAYER
                            ? handleCancelRecording
                            : showCancelEpisodeDialogCallback,
                    );
                    break;
                case ManageRecordingItemKeys.ProtectRecording:
                    onRepeatActionPress(
                        NPVRAction.PROTECT,
                        handleProtectRecording,
                    );
                    break;
                case ManageRecordingItemKeys.UnprotectRecording:
                    onRepeatActionPress(NPVRAction.UNPROTECT, () =>
                        handleShowPinDialog(PinDialogType.UNPROTECT),
                    );
                    break;
                case ManageRecordingItemKeys.DeleteRecording:
                    onRepeatActionPress(
                        NPVRAction.DELETE,
                        showDeleteRecordingDialogCallback,
                    );
                    break;
                // series recordings
                case ManageRecordingItemKeys.ScheduleSeries:
                    onRepeatActionPress(
                        NPVRAction.SCHEDULE_SERIES,
                        handleScheduleSeriesRecording,
                    );
                    break;
                case ManageRecordingItemKeys.CancelSeries:
                    onRepeatActionPress(
                        NPVRAction.DELETE_SERIES,
                        type === RecordingType.PLAYER
                            ? handleCancelSeriesRecording
                            : showCancelSeriesDialogCallback,
                    );
                    break;
                case ManageRecordingItemKeys.CancelEpisode:
                    onRepeatActionPress(
                        NPVRAction.DELETE,
                        type === RecordingType.PLAYER
                            ? handleCancelRecording
                            : showCancelEpisodeDialogCallback,
                    );
                    break;
                case ManageRecordingItemKeys.DeleteSeries:
                    onRepeatActionPress(
                        NPVRAction.DELETE_SERIES,
                        showDeleteSeriesDialogCallback,
                    );
                    break;
                default:
                    throw new Error(
                        `Unhandled Recording Button Press of type ${button.key}`,
                    );
            }

            setTimeout(() => {
                setCanCloseButtonDropdown(true);
            }, 0);
        },
        [
            handleStartRecording,
            showCancelEpisodeDialogCallback,
            handleProtectRecording,
            showDeleteRecordingDialogCallback,
            handleScheduleSeriesRecording,
            type,
            showDeleteSeriesDialogCallback,
            handleCancelSeriesRecording,
            showCancelSeriesDialogCallback,
            handleCancelRecording,
            onRepeatActionPress,
        ],
    );

    return {
        // single recording data
        recordingItem,
        recordingMetadata,
        isRecordingLoading,
        recordingIsRecorded,
        recordingIsValid,
        recordingIsProtected,
        recordingIsFailed,
        recordingHasStatusRecording,
        canWatchRecording,
        eventId,
        seriesHasProtectedRecording,

        // series recording data
        seriesId,
        seriesRecordingId: seriesRecordingIdFromRecordingItem,
        seriesStatus,
        canScheduleSeries,
        canCancelSeries,
        canDeleteSeries,

        // handlers for single recording
        handleStartRecording,
        handleCancelRecording,
        handleProtectRecording,
        handleUnprotectRecording,
        handleDeleteRecording,

        // handlers for series recording
        handleScheduleSeriesRecording,
        handleCancelSeriesRecording,
        handleDeleteSeriesRecording,

        // dialogs and notifications
        recordingsNotification: notification,
        showQuotaDialog,
        showCancelEpisodeDialog,
        showCancelSeriesDialog,
        onCallbackSetNotification,
        resetNotificationCallback,
        showCancelEpisodeDialogCallback,
        hideCancelEpisodeDialogCallback,
        hideCancelSeriesDialogCallback,
        hideDeleteRecordingDialogCallback,
        hideDeleteSeriesDialogCallback,
        hideQuotaDialogCallback,
        showDeleteRecordingDialog,
        showDeleteSeriesDialog,

        // buttons
        recordingsButtonsList,
        onManageRecordingPress,

        // dropdown
        isRecordingDropdownVisible: isDropdownVisible,
        showRecordingDropdown: showDropdown,
        hideRecordingDropdown: hideDropdown,

        // permissions
        recordingPermissionsState,
        hasGlobalRecordingPermission:
            recordingPermissionsState?.hasGlobalRecordingPermission,
        getAssetTitle,
    };
}

export default useRecordings;

export type UseRecordingsHookReturnType = ReturnType<typeof useRecordings>;
